import { motion } from 'framer-motion';
import { ReactNode } from 'react';
import { ArrowUpRight, TrendingUp, TrendingDown } from 'lucide-react';

import { GLASS_CARD } from '@/components/ui/glass';

export type Tono = 'blue' | 'violet' | 'green' | 'red' | 'amber' | 'slate';

interface Props {
  titulo: string;
  valor: number | string;
  icono: ReactNode;
  tono?: Tono;
  delta?: number | null;
  textoComparacion?: string;
  invertido?: boolean;
  etiquetaFija?: string;
  destacado?: boolean;
  className?: string;
  wrapperClassName?: string;
  isMoney?: boolean;
  onClick?: () => void;
  linkLabel?: string;
}

const ICONO_OSCURO =
  'dark:bg-gradient-to-br dark:from-[#2170b8] dark:to-[#1e5a8a] dark:text-white dark:shadow-[0_4px_12px_-2px_rgba(30,90,138,0.5),inset_0_1px_0_rgba(255,255,255,0.22)]';

const TONOS: Record<Tono, string> = {
  blue: `bg-[#2170b8]/10 text-[#2170b8] ${ICONO_OSCURO}`,
  violet: `bg-violet-500/10 text-violet-600 ${ICONO_OSCURO}`,
  green: `bg-emerald-500/10 text-emerald-600 ${ICONO_OSCURO}`,
  red: `bg-red-500/10 text-red-600 ${ICONO_OSCURO}`,
  amber: `bg-amber-500/10 text-amber-600 ${ICONO_OSCURO}`,
  slate: `bg-slate-500/10 text-slate-600 ${ICONO_OSCURO}`,
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 320, damping: 30, mass: 0.7 },
  },
};

const MetricCard = ({
  titulo,
  valor,
  icono,
  tono = 'blue',
  delta,
  textoComparacion,
  invertido = false,
  etiquetaFija,
  destacado = false,
  className = '',
  wrapperClassName = '',
  isMoney = false,
  onClick,
  linkLabel,
}: Props) => {
  const hasDelta = typeof delta === 'number' && !Number.isNaN(delta);
  const sube = hasDelta && (delta as number) >= 0;
  const bueno = invertido ? !sube : sube;

  const valorMostrado =
    isMoney && typeof valor === 'number'
      ? `${valor.toLocaleString('es-ES', {
          minimumFractionDigits: Number.isInteger(valor) ? 0 : 2,
          maximumFractionDigits: 2,
        })} €`
      : typeof valor === 'number'
      ? valor.toLocaleString('es-ES')
      : valor;

  const clicable = !!onClick;

  return (
    <motion.div variants={cardVariants} className={`h-full ${wrapperClassName}`}>
      <div
        className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} w-full h-full text-left transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(16,50,84,0.35)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)] ${className}`}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/15"
        />

        <div
          className={`flex flex-col h-full justify-start ${
            destacado ? 'p-4 sm:p-5 gap-3' : 'p-3 sm:p-4 gap-2 sm:gap-2.5'
          }`}
        >
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <span
              className={`shrink-0 flex items-center justify-center rounded-lg sm:rounded-xl ${TONOS[tono]} ${
                destacado ? 'w-8 h-8 sm:w-9 sm:h-9' : 'w-6 h-6 sm:w-8 sm:h-8'
              } [&_svg]:w-3.5 [&_svg]:h-3.5 sm:[&_svg]:w-4 sm:[&_svg]:h-4 text-sm font-bold`}
            >
              {icono}
            </span>
            <span
              title={titulo}
              className="flex-1 min-w-0 text-[10.5px] sm:text-xs font-semibold [overflow-wrap:anywhere] text-gray-500 dark:text-gray-400 sm:uppercase sm:tracking-[0.05em] leading-tight line-clamp-2"
            >
              {titulo}
            </span>
            {clicable && (
              <motion.button
                type="button"
                onClick={onClick}
                aria-label={linkLabel ?? titulo}
                title={linkLabel ?? titulo}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                className="tour-metric-link shrink-0 w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 bg-gray-100/70 dark:bg-white/5 hover:bg-[#2170b8] hover:text-white dark:hover:bg-[#2170b8] dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2170b8] transition-colors"
              >
                <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </motion.button>
            )}
          </div>

          <span
            className={`font-bold tracking-tight text-gray-900 dark:text-white leading-none tabular-nums whitespace-nowrap ${
              destacado ? 'text-[1.75rem] sm:text-[2rem]' : 'text-2xl sm:text-[1.7rem]'
            }`}
          >
            {valorMostrado}
          </span>

          {(etiquetaFija || hasDelta) && (
          <div className="min-h-[22px] flex items-center gap-1.5 flex-wrap">
            {etiquetaFija ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-white/5 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                {etiquetaFija}
              </span>
            ) : hasDelta ? (
              <>
                <span
                  className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${
                    bueno
                      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                      : 'bg-red-500/10 text-red-600 dark:text-red-400'
                  }`}
                >
                  {sube ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {Math.abs(delta as number).toLocaleString('es-ES', {
                    maximumFractionDigits: 1,
                  })}
                  %
                </span>
                {textoComparacion && (
                  <span className="hidden sm:inline text-[11px] text-gray-400 dark:text-gray-500 leading-tight">
                    {textoComparacion}
                  </span>
                )}
              </>
            ) : null}
          </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MetricCard;
